// Pure Platzierungs-Logik für Anker-Dropdowns (product-autocomplete):
// Wieviel max-height passt unter/über den Anker in den SICHTBAREN Viewport
// (visualViewport — schrumpft bei offener Tastatur), und muss das Dropdown
// über dem Input öffnen, weil unten zu wenig Platz ist?
//
// Desktop-Invariante: bei genug Platz unterm Anker kommt exakt das bisherige
// Verhalten heraus (unten öffnen, Cap 256px = Tailwind max-h-64) — geklemmt
// wird nur, wenn das Dropdown vorher ohnehin abgeschnitten gewesen wäre.

/** Bisheriger Desktop-Cap: Tailwind `max-h-64` = 256px. */
export const DROPDOWN_MAX_HEIGHT = 256
/** Unter dieser Resthöhe unterm Anker wird nach oben ausgewichen. */
export const MIN_SPACE_BELOW = 160
/** Sinnvolle Mindesthöhe (~2,5 Einträge) — darunter wird nicht geklemmt. */
export const DROPDOWN_MIN_HEIGHT = 96
/** Abstand Dropdown↔Anker (Tailwind `mt-1`/`mb-1`). */
const ANCHOR_GAP = 4
/** Luft zum Viewport-Rand. */
const VIEWPORT_MARGIN = 8

export interface DropdownPlacement {
  /** true → über dem Anker öffnen (`bottom-full mb-1`). */
  openUp: boolean
  /** max-height in px, bereits auf [MIN_HEIGHT, MAX_HEIGHT] geklemmt. */
  maxHeight: number
}

/**
 * @param anchorTop    Input-`getBoundingClientRect().top` (Layout-Viewport-px)
 * @param anchorBottom Input-`getBoundingClientRect().bottom`
 * @param viewportHeight sichtbare Viewport-Höhe (visualViewport.height)
 * @param viewportOffsetTop visualViewport.offsetTop — auf iOS bei offener
 *   Tastatur > 0. getBoundingClientRect liefert LAYOUT-Koordinaten, die
 *   sichtbare Fläche liegt aber zwischen [offsetTop, offsetTop+height].
 *   Ohne diese Korrektur (Default 0 = Desktop/kein Tastatur-Offset) flippt
 *   openUp auf iOS fälschlich, weil spaceBelow scheinbar negativ wird.
 */
export function computeDropdownPlacement(
  anchorTop: number,
  anchorBottom: number,
  viewportHeight: number,
  viewportOffsetTop = 0,
): DropdownPlacement {
  const visibleBottom = viewportOffsetTop + viewportHeight
  const spaceBelow = visibleBottom - anchorBottom - ANCHOR_GAP - VIEWPORT_MARGIN
  const spaceAbove = anchorTop - viewportOffsetTop - ANCHOR_GAP - VIEWPORT_MARGIN
  // Nach oben nur, wenn unten wirklich eng ist UND oben mehr Platz bietet —
  // sonst bleibt es beim gewohnten Öffnen nach unten.
  const openUp = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow
  const space = openUp ? spaceAbove : spaceBelow
  const maxHeight = Math.max(DROPDOWN_MIN_HEIGHT, Math.min(DROPDOWN_MAX_HEIGHT, space))
  return { openUp, maxHeight }
}
